import React, { useEffect, useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    MenuItem,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CategoryApiService from '../../../api/CategoryApiService';
import { ok } from '../../../constants/apiStatusConstants';
import CustomColumnApiService from '../../../api/CustomColumnApiService';
import { useTranslation } from 'react-i18next';
import SaveButton from '../../Common/SaveButton';
import Section from '../../Common/Section';
import { useAsyncCallback } from '../../../hooks/useAsyncCallback';
import { useSnackbar } from '../../Common/SnackbarProvider';

// Server-side enum-like values for the datatype + inputType columns.
// Surfaced as constants so option lists are one place to edit.
const DATA_TYPES = ['string', 'boolean', 'int', 'date'];
const INPUT_TYPES = ['text', 'textarea', 'select', 'checkbox', 'number', 'date'];

// Magic option-string sentinels the Task page handles for user pickers.
// We hide them from the raw options editor and expose them as a discoverable picker instead.
const OPTIONS_SOURCE = {
    CUSTOM: 'custom',
    USER_SINGLE: 'userSingle',
    USER_MULTI: 'userMulti',
};
const USERS_MULTI_SENTINEL = '%users%';
const USERS_SINGLE_SENTINEL = '%user%';

const SOURCE_LOCALE = 'en';
const TRANSLATABLE_LOCALES = [
    { code: 'ru', label: 'Русский' },
    { code: 'fr', label: 'Français' },
];

const categoryApiService = new CategoryApiService();
const customColumnApiService = new CustomColumnApiService();

function detectOptionsSource(rawOptions) {
    if (rawOptions === USERS_MULTI_SENTINEL) return OPTIONS_SOURCE.USER_MULTI;
    if (rawOptions === USERS_SINGLE_SENTINEL) return OPTIONS_SOURCE.USER_SINGLE;
    return OPTIONS_SOURCE.CUSTOM;
}

function parseCustomOptions(rawOptions) {
    if (!rawOptions) return [];
    if (rawOptions === USERS_MULTI_SENTINEL || rawOptions === USERS_SINGLE_SENTINEL) return [];
    return rawOptions.split('|').map(o => o.trim()).filter(Boolean);
}

export default function EditCustomColumnModal (props) {
    const {
        open,
        currentCustomColumn,
        onCancel,
        onSave,
    } = props;
    const { t } = useTranslation();
    const snackbar = useSnackbar();

    const [customColumnName, setCustomColumnName] = useState('');
    const [dataType, setDataType] = useState('string');
    const [dataLength, setDataLength] = useState(3600);
    const [description, setDescription] = useState('');
    const [columnOrder, setColumnOrder] = useState(1);
    const [inputType, setInputType] = useState('text');
    const [categories, setCategories] = useState([]);
    const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
    const [optionsSource, setOptionsSource] = useState(OPTIONS_SOURCE.CUSTOM);
    const [customOptions, setCustomOptions] = useState([]);
    const [optionDraft, setOptionDraft] = useState('');
    const [translations, setTranslations] = useState({});
    const [manualLocales, setManualLocales] = useState([]);
    const [editableByAssignee, setEditableByAssignee] = useState(false);

    const [nameError, setNameError] = useState(false);
    const [optionsError, setOptionsError] = useState(false);

    const isEdit = Boolean(currentCustomColumn);
    const isSelect = inputType === 'select';
    const isCustomOptionList = isSelect && optionsSource === OPTIONS_SOURCE.CUSTOM;

    useEffect(() => {
        if (!open) return;

        setCustomColumnName(currentCustomColumn?.customColumnName || '');
        setDataType(currentCustomColumn?.dataTypeFix || 'string');
        setDataLength(currentCustomColumn?.dataTypeLength || 3600);
        setDescription(currentCustomColumn?.description || '');
        setColumnOrder(currentCustomColumn?.columnOrder || 1);
        setEditableByAssignee(currentCustomColumn?.editableByAssignee ?? false);
        setNameError(false);
        setOptionsError(false);
        setOptionDraft('');

        const incomingInputType = currentCustomColumn?.inputType === 'option' ? 'select' : (currentCustomColumn?.inputType || 'text');
        setInputType(incomingInputType);

        const rawOptions = currentCustomColumn?.options ?? '';
        setOptionsSource(detectOptionsSource(rawOptions));
        setCustomOptions(parseCustomOptions(rawOptions));

        const incomingNames = currentCustomColumn?.names ?? {};
        const nonSource = Object.fromEntries(
            Object.entries(incomingNames).filter(([locale]) => locale !== SOURCE_LOCALE)
        );
        setTranslations(nonSource);
        setManualLocales(currentCustomColumn?.manualLocales ?? []);

        populateCategories().then(() => {
            if (currentCustomColumn?.displayForCategory) {
                setSelectedCategoryIds(currentCustomColumn.displayForCategory.split(',').filter(Boolean).map(Number));
            } else {
                setSelectedCategoryIds([]);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, currentCustomColumn]);

    async function populateCategories() {
        const categoryResponse = await categoryApiService.GetCategories();
        setCategories(categoryResponse.data ?? []);
    }

    function addOption() {
        const trimmed = optionDraft.trim();
        if (!trimmed) return;
        if (customOptions.includes(trimmed)) {
            setOptionDraft('');
            return;
        }
        setCustomOptions(prev => [...prev, trimmed]);
        setOptionDraft('');
        setOptionsError(false);
    }

    function removeOption(value) {
        setCustomOptions(prev => prev.filter(o => o !== value));
    }

    function onTranslationChange(locale, value) {
        setTranslations(prev => ({ ...prev, [locale]: value }));
        if (!manualLocales.includes(locale)) {
            setManualLocales(prev => [...prev, locale]);
        }
    }

    function onClearOverride(locale) {
        setTranslations(prev => {
            const next = { ...prev };
            delete next[locale];
            return next;
        });
        setManualLocales(prev => prev.filter(l => l !== locale));
    }

    function buildOptionsString() {
        if (!isSelect) return '';
        if (optionsSource === OPTIONS_SOURCE.USER_MULTI) return USERS_MULTI_SENTINEL;
        if (optionsSource === OPTIONS_SOURCE.USER_SINGLE) return USERS_SINGLE_SENTINEL;
        return customOptions.join('|');
    }

    function validate() {
        let valid = true;
        if (!customColumnName.trim()) {
            setNameError(true);
            snackbar.error(t('editCustomColumn.validation.nameRequired'));
            valid = false;
        } else {
            setNameError(false);
        }
        if (isCustomOptionList && customOptions.length === 0) {
            setOptionsError(true);
            snackbar.error(t('editCustomColumn.validation.optionsRequired'));
            valid = false;
        } else {
            setOptionsError(false);
        }
        return valid;
    }

    function buildPayload() {
        const displayCategories = selectedCategoryIds.join(',');
        const finalDataType = dataType === 'string' ? `${dataType},${dataLength}` : dataType;
        const finalInputType = isSelect ? 'option' : inputType;

        return {
            customColumnName,
            displayForCategory: displayCategories,
            datatype: finalDataType,
            inputType: finalInputType,
            description,
            options: buildOptionsString(),
            columnOrder,
            editableByAssignee,
            names: { [SOURCE_LOCALE]: customColumnName, ...translations },
            manualLocales,
        };
    }

    const { run: handleSave, loading: saving } = useAsyncCallback(async () => {
        if (!validate()) return;

        const payload = buildPayload();
        let response;

        if (isEdit) {
            payload.customColumnId = currentCustomColumn.customColumnId;
            response = await customColumnApiService.updateCustomColumn(payload);
            if (response.status === ok) {
                snackbar.success(t('editCustomColumn.toast.updated', { name: customColumnName }));
                onSave();
                return;
            }
        } else {
            response = await customColumnApiService.addCustomColumn(payload);
            if (response.status === ok) {
                snackbar.success(t('editCustomColumn.toast.created', { name: customColumnName }));
                onSave();
                return;
            }
        }

        snackbar.error(t('editCustomColumn.toast.saveFailed'));
    });

    const title = useMemo(() => {
        if (currentCustomColumn?.customColumnName) {
            return t('editCustomColumn.title.edit', { name: currentCustomColumn.customColumnName });
        }
        return isEdit ? t('editCustomColumn.title.editFallback') : t('editCustomColumn.title.create');
    }, [currentCustomColumn, isEdit, t]);

    const translationsHelper = useMemo(() => isEdit
        ? t('editCustomColumn.translations.helperEdit')
        : t('editCustomColumn.translations.helperCreate'), [isEdit, t]);

    const selectedCategoryObjects = useMemo(
        () => categories.filter(c => selectedCategoryIds.includes(c.ctId)),
        [categories, selectedCategoryIds]
    );

    return (
        <Dialog maxWidth="md" fullWidth open={open}>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent dividers>
                <Box sx={{ pt: 1 }}>
                    <Section title={t('editCustomColumn.section.identity')}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label={t('editCustomColumn.field.name')}
                                value={customColumnName}
                                onChange={(e) => setCustomColumnName(e.target.value)}
                                error={nameError}
                                helperText={nameError ? t('editCustomColumn.validation.nameRequired') : ''}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                minRows={2}
                                label={t('editCustomColumn.field.description')}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                            />
                        </Grid>
                    </Section>

                    <Section title={t('editCustomColumn.section.type')}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                fullWidth
                                label={t('editCustomColumn.field.dataType')}
                                value={dataType}
                                onChange={(e) => setDataType(e.target.value)}
                            >
                                {DATA_TYPES.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                fullWidth
                                label={t('editCustomColumn.field.inputType')}
                                value={inputType}
                                onChange={(e) => setInputType(e.target.value)}
                            >
                                {INPUT_TYPES.map(i => <MenuItem key={i} value={i}>{i}</MenuItem>)}
                            </TextField>
                        </Grid>
                        {dataType === 'string' && (
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label={t('editCustomColumn.field.length')}
                                    value={dataLength}
                                    onChange={(e) => setDataLength(parseInt(e.target.value) || 0)}
                                    inputProps={{ min: 1 }}
                                />
                            </Grid>
                        )}
                        {isSelect && (
                            <>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        select
                                        fullWidth
                                        label={t('editCustomColumn.field.optionsSourceType')}
                                        value={optionsSource}
                                        onChange={(e) => setOptionsSource(e.target.value)}
                                    >
                                        <MenuItem value={OPTIONS_SOURCE.CUSTOM}>{t('editCustomColumn.optionsSource.custom')}</MenuItem>
                                        <MenuItem value={OPTIONS_SOURCE.USER_SINGLE}>{t('editCustomColumn.optionsSource.userSingle')}</MenuItem>
                                        <MenuItem value={OPTIONS_SOURCE.USER_MULTI}>{t('editCustomColumn.optionsSource.userMulti')}</MenuItem>
                                    </TextField>
                                </Grid>
                                {isCustomOptionList && (
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                            {t('editCustomColumn.field.options')}
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1, minHeight: 32 }}>
                                            {customOptions.length === 0
                                                ? <Typography variant="caption" color="text.secondary">{t('editCustomColumn.option.empty')}</Typography>
                                                : customOptions.map((opt) => (
                                                    <Chip
                                                        key={opt}
                                                        label={opt}
                                                        onDelete={() => removeOption(opt)}
                                                        deleteIcon={<CloseRoundedIcon />}
                                                    />
                                                ))
                                            }
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <TextField
                                                size="small"
                                                fullWidth
                                                value={optionDraft}
                                                onChange={(e) => setOptionDraft(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        addOption();
                                                    }
                                                }}
                                                placeholder={t('editCustomColumn.option.addPlaceholder')}
                                                error={optionsError}
                                                helperText={optionsError ? t('editCustomColumn.validation.optionsRequired') : ''}
                                            />
                                            <Button
                                                variant="outlined"
                                                onClick={addOption}
                                                disabled={!optionDraft.trim()}
                                                startIcon={<AddRoundedIcon />}
                                            >
                                                {t('editCustomColumn.option.add')}
                                            </Button>
                                        </Box>
                                    </Grid>
                                )}
                            </>
                        )}
                    </Section>

                    <Section title={t('editCustomColumn.section.display')}>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                type="number"
                                label={t('editCustomColumn.field.columnOrder')}
                                value={columnOrder}
                                onChange={(e) => setColumnOrder(parseInt(e.target.value) || 0)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={8}>
                            <Autocomplete
                                multiple
                                options={categories}
                                getOptionLabel={(option) => option?.localizedName ?? option?.ctName ?? ''}
                                isOptionEqualToValue={(option, value) => option?.ctId === value?.ctId}
                                value={selectedCategoryObjects}
                                onChange={(_e, newValue) => setSelectedCategoryIds(newValue.map(c => c.ctId))}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <Chip
                                            label={option.localizedName ?? option.ctName}
                                            size="small"
                                            {...getTagProps({ index })}
                                            key={option.ctId}
                                        />
                                    ))
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t('editCustomColumn.field.taskCategories')}
                                    />
                                )}
                            />
                        </Grid>
                    </Section>

                    <Section title={t('editCustomColumn.section.permissions')}>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={editableByAssignee}
                                        onChange={(e) => setEditableByAssignee(e.target.checked)}
                                    />
                                }
                                label={t('editCustomColumn.field.editableByAssignee')}
                            />
                            <Typography variant="caption" color="text.secondary" display="block">
                                {t('editCustomColumn.field.editableByAssigneeHelper')}
                            </Typography>
                        </Grid>
                    </Section>

                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle1" fontWeight={600}>
                        {t('editCustomColumn.translations.title')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                        {translationsHelper}
                    </Typography>
                    <Stack spacing={1.5}>
                        {TRANSLATABLE_LOCALES.map(({ code, label }) => {
                            const isManual = manualLocales.includes(code);
                            return (
                                <Box key={code} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography sx={{ minWidth: 90, fontWeight: 500 }}>{label}</Typography>
                                    <TextField
                                        size="small"
                                        sx={{ flex: 1 }}
                                        value={translations[code] ?? ''}
                                        placeholder={isEdit ? '' : t('editCustomColumn.translations.willGenerate')}
                                        onChange={(e) => onTranslationChange(code, e.target.value)}
                                        InputProps={{
                                            endAdornment: isManual ? (
                                                <Tooltip title={t('editCustomColumn.translations.regenerateTooltip')}>
                                                    <IconButton size="small" onClick={() => onClearOverride(code)}>
                                                        <RefreshRoundedIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            ) : null,
                                        }}
                                        helperText={isManual ? t('editCustomColumn.translations.manualBadge') : t('editCustomColumn.translations.autoBadge')}
                                    />
                                </Box>
                            );
                        })}
                    </Stack>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button autoFocus variant="outlined" onClick={onCancel} disabled={saving}>
                    {t('common.cancel')}
                </Button>
                <SaveButton loading={saving} variant="contained" onClick={handleSave}>
                    {t('common.save')}
                </SaveButton>
            </DialogActions>
        </Dialog>
    );
}
